import { useEffect, useRef } from "react";

/**
 * Attach the returned ref to a section element; `onVisible` fires at most
 * once for that mount, the moment the section crosses `threshold`. The
 * observer disconnects itself after firing — no scroll listeners, no
 * repeat fires while the section stays in view.
 *
 * `onVisible` is read from a ref internally, so callers can pass an inline
 * arrow function without re-subscribing the observer on every render.
 *
 * @template {HTMLElement} [T=HTMLElement]
 * @param {() => void} onVisible
 * @param {{ threshold?: number, rootMargin?: string }} [options]
 * @returns {import("react").RefObject<T | null>}
 */
export function useSectionOnceObserver(onVisible, options = {}) {
  const ref = useRef(null);
  const hasFiredRef = useRef(false);
  const onVisibleRef = useRef(onVisible);

  // Sync the ref after render (not during it) — writing to ref.current in
  // the render body itself is a purity violation under React's rules.
  useEffect(() => {
    onVisibleRef.current = onVisible;
  });

  const { threshold = 0.3, rootMargin = "0px" } = options;

  useEffect(() => {
    const node = ref.current;
    if (!node || hasFiredRef.current) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !hasFiredRef.current) {
          hasFiredRef.current = true;
          onVisibleRef.current();
          observer.disconnect();
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return ref;
}
